// Core
import React, { useState } from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import { RequestMethod, requestVantageApi } from '../../../../../plugins/request';

// Components
import { Col } from '../../../../../layout/elements/Col';
import { Drawer } from '../../../../../layout/elements/Drawer';
import { FileUpload } from '../../../../../layout/elements/FileUpload';
import { Input } from '../../../../../layout/elements/Input';
import { Paragraph, ParagraphWeight } from '../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../layout/elements/Row';
import {
  Select,
  SelectSizeType,
  SelectTagsMode,
} from '../../../../../layout/elements/Select';
import { showToast, ToastType } from '../../../../../layout/elements/Toast';
import { AtLine } from '../../../../../layout/icons';

// View
import { CreateCustomerProps } from './Create.types';
import {
  CreateContainer,
  FileUploadContainer,
  FormFieldsContainer,
} from './Create.style';

// Types
import { Customer } from '../../../../../types/global';

function CreateCustomer({
  className,
  isDrawerOpen,
  onCloseDrawer,
  onUpdateData,
  testId,
}: CreateCustomerProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  /* ***********************************************************************************************
  *************************************** LOCAL STATES *********************************************
  *********************************************************************************************** */

  interface LogoFile {
    source?: string;
  }

  interface State {
    isLoading: number | true | null;
    name?: string | number | null;
    domains?: string | string [] | null;
    logoError?: string | null;
    logoFile?: LogoFile | null;
  }

  const initialState: State = {
    isLoading: null,
    name: null,
    domains: null,
    logoError: null,
    logoFile: null,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  *********************************************** METHODS ******************************************
  *********************************************************************************************** */

  const startDrawerLoading = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      isLoading: true,
    }));
  };

  const stopDrawerLoading = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      isLoading: 100,
    }));
  };

  const resetCustomerForm = (): void => {
    setState({
      name: null,
      domains: null,
      logoError: null,
      logoFile: null,
      isLoading: null,
    });
  };

  // Handle logo file upload
  const handleLogoUpload = (file: LogoFile): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoError: null,
      logoFile: file,
    }));
  };

  // Handle logo file removal
  const handleLogoRemove = (): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoError: null,
      logoFile: null,
    }));
  };

  // Handle logo upload error
  const handleLogoError = (errorMessage: string): void => {
    setState((prevState: State): State => ({
      ...prevState,
      logoError: errorMessage,
    }));
  };

  /* Request */
  const onCreate = async (): Promise<void> => {
    startDrawerLoading();

    try {
      const requestBody: {
        name: string;
        domains: string[];
        logoUpload?: string;
      } = {
        name: state.name as string,
        domains: (state.domains || []) as string[],
      };

      if (state.logoFile?.source) {
        requestBody.logoUpload = state.logoFile.source;
      }

      const response: Customer = await requestVantageApi.customer({
        method: RequestMethod.POST,
        path: '/customer',
        body: requestBody,
      });

      await requestVantageApi.adminJobs({
        method: RequestMethod.POST,
        path: `/database/run-customer-migrations/${response.id}`,
      });

      showToast({
        text: t('view.customers.create.customerCreated', { customer: state.name }),
        type: ToastType.success,
      });

      stopDrawerLoading();

      setTimeout((): void => {
        onCloseDrawer();
        onUpdateData();
        resetCustomerForm();
      }, 300);
    } catch (error: unknown) {
      stopDrawerLoading();
      onCloseDrawer();
      resetCustomerForm();
    }
  };

  // Trigger onFieldChange based on fieldId
  const handleFieldChange = (fieldId: string, value: string | string[]): void => {
    setState((prevState: State): State => ({
      ...prevState,
      [fieldId]: value,
    }));
  };

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.customers.newCustomer')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      primaryAction={{
        children: t('common.create'),
        onClick: onCreate,
        disabled: !state.name || !(state.domains && state.domains.length) || !!state.logoError,
        loading: state.isLoading,
        loadingText: `${t('common.creating')}`,
      }}
      secondaryAction={{ children: t('common.cancel'), onClick: onCloseDrawer }}
    >

      <Row style={{ flex: 1 }}>
        <Col>
          <CreateContainer>
            <Paragraph
              weight={ParagraphWeight.bold}
              testId="customer-logo-section"
            >
              {t('view.customers.create.logo')}
            </Paragraph>

            <FileUploadContainer>
              <FileUpload
                id="customer-logo-upload"
                file={state.logoFile || { source: undefined }}
                onUpload={handleLogoUpload}
                onRemove={handleLogoRemove}
                onError={handleLogoError}
                maxSize={1 * 1024 * 1024} // 1MB
                error={!!state.logoError}
                errorText={state.logoError}
                supportingText={`${t('view.customers.create.supportingText')}`}
                accept=".jpg, .png, .gif, .jpeg, .avif, .webp"
              />
            </FileUploadContainer>

            {/* @TODO: uncomment this in the future, apply the logic and translate */}
            {/* <CoverContainer> */}
            {/*  <Paragraph */}
            {/*    weight={ParagraphWeight.bold} */}
            {/*    testId="customer-cover-section" */}
            {/*  > */}
            {/*    Customer Cover */}
            {/*  </Paragraph> */}
            {/* </CoverContainer> */}

            {/* <ActionsUploadContainer> */}
            {/*  <Button */}
            {/*    type={ButtonType.secondary} */}
            {/*    color={ButtonColor.primary} */}
            {/*    size={ButtonSize.xs} */}
            {/*    onClick={() => {}} */}
            {/*  > */}
            {/*    Gallery */}
            {/*  </Button> */}
            {/*  <Button */}
            {/*    type={ButtonType.secondary} */}
            {/*    color={ButtonColor.primary} */}
            {/*    size={ButtonSize.xs} */}
            {/*    onClick={() => {}} */}
            {/*  > */}
            {/*    Upload */}
            {/*  </Button> */}
            {/* </ActionsUploadContainer> */}

            {/* <FileUploadContainer> */}
            {/*  <FileUpload */}
            {/*    onUpload={() => { */}
            {/*    }} */}
            {/*    file={blankFile} */}
            {/*    id="customer-cover" */}
            {/*    onRemove={() => {}} */}
            {/*    supportingText=".jpg .png .gif .jpeg (Max. 1MB)" */}
            {/*    accept=".jpg, .png, .gif, .jpeg" */}
            {/*  /> */}
            {/* </FileUploadContainer> */}

            <Paragraph
              weight={ParagraphWeight.bold}
              testId="main-info-section"
            >
              {t('common.mainInfo')}
            </Paragraph>

            <FormFieldsContainer>
              <Input
                placeholder={`${t('common.name')}`}
                label={`${t('common.name')}`}
                value={state.name}
                onChange={(newValue) => {
                  if (typeof newValue === 'string') {
                    handleFieldChange('name', newValue);
                  }
                }}
                maxLength={65}
                testId="input-customer-name"
              />

              <div>
                <Select
                  testId="select-customer-domains"
                  label={`${t('view.customers.create.domain')}`}
                  mode={SelectTagsMode.tags}
                  placeholder={`${t('view.customers.create.placeholderDomain')}`}
                  onChange={(value: string | string[]) => handleFieldChange('domains', value)}
                  size={SelectSizeType.lg}
                  value={state.domains ? state.domains : undefined}
                  supportingText={`${t('view.customers.create.domainsSupportingText')}`}
                  prefix={<AtLine />}
                />
              </div>
            </FormFieldsContainer>
          </CreateContainer>

          {/* @TODO: apply in the future */}
          {/* <TechnicalInfoContainer> */}
          {/*  <Paragraph */}
          {/*    weight={ParagraphWeight.bold} */}
          {/*  > */}
          {/*    Technical Info */}
          {/*  </Paragraph> */}
          {/* </TechnicalInfoContainer> */}

          {/* <OCPServerContainer> */}
          {/*  <Switch */}
          {/*    size={SwitchWithTextSize.md} */}
          {/*    onChange={() => {}} */}
          {/*    text="OCP Server" */}
          {/*  /> */}
          {/* </OCPServerContainer> */}
        </Col>
      </Row>
    </Drawer>
  );
}

export { CreateCustomer };
